/**
 * Спільне для списку закриттів і сторінки документа закриття (ч.1031):
 * підписи статусів/операцій/зауважень і форматер підсумку операції.
 * Живе окремо, щоб список (`MonthCloseRunsPage`) і документ
 * (`MonthCloseRunPage`) показували ОДНАКОВІ слова.
 */
import type { BadgeTone } from '@/shared/components/DocStatusBadge';

/** ч.1028: статуси — бейджами `DocStatusBadge` (тони вже під нічну тему). */
export const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'очікує', tone: 'gray' },
  IN_PROGRESS: { label: 'виконується', tone: 'blue' },
  COMPLETED: { label: 'завершено', tone: 'green' },
  PARTIAL: { label: 'частково', tone: 'amber' },
  FAILED: { label: 'помилка', tone: 'red' },
  // ч.1120: документ розпроведено — усе, що він створив, прибрано.
  CANCELLED: { label: 'розпроведено', tone: 'gray' },
};
export const badge = (st: string) => STATUS[st] ?? { label: st, tone: 'gray' as BadgeTone };

/** Операції раннера — людською мовою (у результаті прогону). */
export const OP_LABELS: Record<string, string> = {
  AMORTIZATION: 'Амортизація',
  PAYROLL: 'Заробітна плата',
  FX_REVALUATION: 'Переоцінка валюти',
  OVERHEAD_ALLOCATION: 'Розподіл загальновиробничих',
  PRODUCTION_COST_CLOSING: 'Собівартість виробництва',
  EXPENSE_CLOSING: 'Закриття витрат',
  REVENUE_CLOSING: 'Закриття доходів',
  FINANCIAL_RESULT_CLOSING: 'Фінансовий результат',
  // ч.1134: річне закриття рахунку 443.
  USED_PROFIT_CLOSING: 'Використаний прибуток (рахунок 443)',
  QUARTERLY_SNAPSHOT: 'Квартальний знімок',
  // ч.1120: кроки ФОП і дні відпустки — людськими назвами (були сирі коди).
  SINGLE_TAX: 'Єдиний податок',
  FOP_SELF_ESV: 'ЄСВ підприємця «за себе»',
  FOP_SELF_WZ: 'Військовий збір підприємця «за себе»',
  FOP_ZSO_QUARTERLY: 'Податки підприємця на загальній системі',
  VACATION_ACCRUAL: 'Дні відпустки',
};

/** ч.1030: підсумок операції — ЛЮДСЬКОЮ мовою для кожного типу.
 *  Раніше форматувався лише 1 тип, решта — сирий JSON
 *  ({"total_net":"6545.00",…}) прямо бухгалтеру у вічі. */
const uah = (v: unknown): string => {
  const n = Number(v ?? 0);
  return `${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
};
const num = (v: unknown): number => Number(v ?? 0);

export function formatOpSummary(opType: string, s: Record<string, unknown>): string {
  // Пропуск — причина вже сформульована на бекенді людською мовою.
  if (s.reason) return String(s.reason);
  if (s.skipped_reason) return String(s.skipped_reason);

  const parts: string[] = [];
  switch (opType) {
    case 'AMORTIZATION':
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      parts.push(`нараховано ${uah(s.total_amount)}`);
      parts.push(`активів: ${num(s.items_count)}`);
      if (num(s.skipped_count) > 0) parts.push(`без нарахування: ${num(s.skipped_count)}`);
      break;
    case 'PAYROLL':
      if (s.document_number) parts.push(`відомість ${s.document_number}`);
      parts.push(`працівників: ${num(s.employees_count)}`);
      parts.push(`нараховано ${uah(s.total_gross)}`);
      parts.push(`до виплати ${uah(s.total_net)}`);
      // ч.1120: кому зарплату вже нараховано іншим документом — не подвоюємо.
      if (num(s.already_accrued_count) > 0) {
        parts.push(`уже нараховано раніше: ${num(s.already_accrued_count)}`);
      }
      break;
    case 'FX_REVALUATION':
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      if (s.total_amount !== undefined) parts.push(`курсова різниця ${uah(s.total_amount)}`);
      break;
    case 'OVERHEAD_ALLOCATION':
      parts.push(`розподілено ${uah(s.allocated)}`);
      parts.push(`проводок: ${num(s.entries_count)}`);
      break;
    case 'PRODUCTION_COST_CLOSING': {
      const groups = Array.isArray(s.groups) ? (s.groups as Record<string, unknown>[]) : [];
      const remaining = groups.reduce((acc, g) => acc + num(g.wip_remaining), 0);
      parts.push(`відхилення від планової собівартості ${uah(s.total_variance)}`);
      parts.push(`продукції: ${num(s.entries_count)}`);
      if (remaining > 0) parts.push(`незавершене виробництво ${uah(remaining)}`);
      break;
    }
    case 'EXPENSE_CLOSING': {
      const rows = Array.isArray(s.subconto_breakdown)
        ? (s.subconto_breakdown as Record<string, unknown>[]) : [];
      // Групуємо за рахунком: «91 — 9 554,13; 92 — 10 402,34».
      const byAcc = new Map<string, number>();
      for (const r of rows) byAcc.set(String(r.account_code), (byAcc.get(String(r.account_code)) ?? 0) + num(r.amount));
      parts.push(`списано на фінрезультат ${uah(s.total_amount)}`);
      if (byAcc.size) parts.push([...byAcc].map(([a, v]) => `рах. ${a} — ${uah(v)}`).join(', '));
      break;
    }
    case 'REVENUE_CLOSING':
      parts.push(num(s.total_amount) > 0
        ? `списано на фінрезультат ${uah(s.total_amount)}`
        : 'доходів за період не було');
      break;
    case 'FINANCIAL_RESULT_CLOSING': {
      const fr = num(s.financial_result);
      parts.push(fr >= 0 ? `прибуток ${uah(fr)}` : `збиток ${uah(-fr)}`);
      if (s.transfer_account) parts.push(`віднесено на рахунок ${s.transfer_account}`);
      break;
    }
    case 'USED_PROFIT_CLOSING': {
      // Дебетове сальдо 443 зі знаком: додатне — використано більше, ніж внесено.
      const used = num(s.used_profit);
      if (used >= 0) {
        parts.push(`закрито ${uah(used)}`);
        if (num(s.from_profit) > 0) parts.push(`за рахунок нерозподіленого прибутку (441) — ${uah(s.from_profit)}`);
        if (num(s.to_loss) > 0) parts.push(`на непокритий збиток (442) — ${uah(s.to_loss)}`);
      } else {
        parts.push(`внесено більше, ніж використано: ${uah(-used)}`);
        if (num(s.covers_loss) > 0) parts.push(`зменшено непокритий збиток (442) — ${uah(s.covers_loss)}`);
        if (num(s.to_profit) > 0) parts.push(`додано до нерозподіленого прибутку (441) — ${uah(s.to_profit)}`);
      }
      break;
    }
    case 'QUARTERLY_SNAPSHOT':
      parts.push(`з початку року: доходи ${uah(s.ytd_revenue)}, витрати ${uah(s.ytd_expenses)}`);
      parts.push(`фінрезультат ${uah(s.ytd_financial_result)}`);
      break;
    case 'SINGLE_TAX':
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      if (s.tax_amount !== undefined) parts.push(`податок ${uah(s.tax_amount)}`);
      break;
    case 'FOP_SELF_ESV':
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      if (s.esv_amount !== undefined) parts.push(`нараховано ${uah(s.esv_amount)}`);
      break;
    case 'FOP_SELF_WZ':
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      if (s.wz_amount !== undefined) parts.push(`нараховано ${uah(s.wz_amount)}`);
      break;
    case 'FOP_ZSO_QUARTERLY':
      if (s.net_income !== undefined) parts.push(`чистий дохід ${uah(s.net_income)}`);
      if (s.pdfo_amount !== undefined) parts.push(`податок на доходи ${uah(s.pdfo_amount)}`);
      if (s.wz_amount !== undefined) parts.push(`військовий збір ${uah(s.wz_amount)}`);
      if (s.esv_amount !== undefined) parts.push(`ЄСВ ${uah(s.esv_amount)}`);
      break;
    case 'VACATION_ACCRUAL':
      parts.push(`нараховано працівникам: ${num(s.accruals_count)}`);
      break;
    default: {
      // Невідомий тип: показуємо лише зрозумілі ключі, без сирого JSON.
      if (s.document_number) parts.push(`документ ${s.document_number}`);
      if (s.total_amount !== undefined) parts.push(`сума ${uah(s.total_amount)}`);
      if (s.entries_count !== undefined) parts.push(`проводок: ${num(s.entries_count)}`);
    }
  }
  return parts.join(' · ');
}

/** ч.1027: pre-check показував сирий код типу (VACATION_ORDER_WITHOUT_CALC: 2)
 *  — бухгалтеру це ні про що. Підписи людською мовою. */
export const WARNING_LABELS: Record<string, string> = {
  VACATION_ORDER_WITHOUT_CALC: 'Накази на відпустку без розрахунку',
  SICK_LEAVE_WITHOUT_CALC: 'Лікарняні без розрахунку',
  FX_REVALUATION_MISSING: 'Немає переоцінки валюти за період',
  // CHK-013: штатні без дати прийому і без кадрових документів — ЗП їх
  // пропустить мовчки. Рішення власника (ч.1027): попереджати, не підміняти дати.
  STAFF_WITHOUT_HIRE_HISTORY:
    'Штатні без дати прийому та кадрових документів — у ЗП НЕ потраплять',
};


/** ч.1120: куди веде документ, створений кроком закриття (null — посилання немає). */
export function stepDocumentLink(opType: string, documentId: string | null): { to: string; label: string } | null {
  if (!documentId) return null;
  switch (opType) {
    case 'FX_REVALUATION': return { to: `/fx-revaluations/${documentId}`, label: 'відкрити переоцінку' };
    case 'PAYROLL': return { to: `/payrolls/${documentId}`, label: 'відкрити нарахування' };
    case 'AMORTIZATION': return { to: '/assets/depreciation-journal', label: 'журнал амортизації' };
    default: return null;
  }
}

/** Дата YYYY-MM-DD → дд.мм.рррр (без зсуву часового поясу). */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}
